"use client";

import * as React from "react";
import { cn } from "@repo/utils/cn";
import { tv, VariantProps } from "tailwind-variants";
import { sectionWrapper } from "../sectionWrappers";
import { Dropdown, DropdownTrigger, DropdownOption } from "../Dropdown";
import { SearchField } from "../SearchField";

const filterSection = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:gap-[var(--container-gutter)] uipm:items-stretch",
    "uipm:w-full"
  ),
});

const filtersLayout = tv({
  base: "uipm:flex uipm:flex-row uipm:gap-[var(--container-margin)] uipm:items-stretch uipm:w-full",
});

const dropdownTriggerStyles = tv({
  base: cn(
    "uipm:bg-[var(--surface-surface-secondary)] uipm:flex uipm:flex-row uipm:gap-[var(--container-gutter)]",
    "uipm:items-center uipm:justify-between uipm:p-4 uipm:rounded-xl",
    "uipm:min-h-[58px] uipm:flex-1 uipm:w-full",
  ),
});

export type FilterField = {
  type: "dropdown" | "search";
  name: string;
  placeholder?: string;
  options?: (DropdownOption & {
    _editable?: React.HTMLAttributes<HTMLElement>;
  })[];
  _editable?: React.HTMLAttributes<HTMLElement>;
};

export type FilterValues = Record<string, string>;

export type FilterChangeHandler = (fieldName: string, value: string) => void;

export interface FilterSectionRef {
  clearField: (fieldName: string) => void;
  clearAll: () => void;
  getValues: () => FilterValues;
  setValue: (fieldName: string, value: string) => void;
}

export type FilterSectionProps = VariantProps<typeof sectionWrapper> &
  VariantProps<typeof filterSection> & {
    id?: string;
    fields?: FilterField[];
    className?: string;
    inputBackground?: "default" | "white";
    initialValues?: FilterValues;
    onFiltersChange?: (filters: FilterValues) => void;
    onFieldChange?: FilterChangeHandler;
    ref?: React.Ref<FilterSectionRef>;
  };

export const FilterSection = React.forwardRef<
  FilterSectionRef,
  FilterSectionProps
>(
  (
    {
      fields = [],
      className,
      backgroundColor = "none",
      topPadding = "none",
      bottomPadding = "none",
      inputBackground = "default",
      initialValues = {},
      onFiltersChange,
      onFieldChange,
      id,
      ...props
    },
    ref,
  ) => {
    const [filterValues, setFilterValues] =
      React.useState<FilterValues>(initialValues);

    React.useEffect(() => {
      setFilterValues(initialValues);
    }, [initialValues]);

    const handleFieldChange: FilterChangeHandler = React.useCallback(
      (fieldName: string, value: string) => {
        setFilterValues((prev) => {
          const newValues = { ...prev, [fieldName]: value };

          onFieldChange?.(fieldName, value);

          onFiltersChange?.(newValues);
          return newValues;
        });
      },
      [onFiltersChange, onFieldChange],
    );

    const handleFieldClear = React.useCallback(
      (fieldName: string) => {
        handleFieldChange(fieldName, "");
      },
      [handleFieldChange],
    );

    const handleClearAll = React.useCallback(() => {
      const clearedValues: FilterValues = {};
      fields.forEach((field) => {
        clearedValues[field.name] = "";
      });
      setFilterValues(clearedValues);
      onFiltersChange?.(clearedValues);
    }, [fields, onFiltersChange]);

    React.useImperativeHandle(
      ref,
      () => ({
        clearField: handleFieldClear,
        clearAll: handleClearAll,
        getValues: () => filterValues,
        setValue: handleFieldChange,
      }),
      [handleFieldClear, handleClearAll, filterValues, handleFieldChange],
    );

    const renderDropdown = React.useCallback(
      (field: FilterField) => {
        const fieldValue = filterValues[field.name] || "";
        const selectedOption = fieldValue
          ? field.options?.find((opt) => opt.value === fieldValue)
          : undefined;

        return (
          <div
            key={field.name}
            className="uipm:flex-1"
            {...(field._editable || {})}
          >
            <Dropdown
              options={field.options || []}
              value={fieldValue}
              onValueChange={(value) => handleFieldChange(field.name, value)}
              showNoneOption={true}
              noneOptionLabel={field.placeholder || "None selected"}
              trigger={
                <DropdownTrigger
                  placeholder={field.placeholder || "Placeholder"}
                  selectedLabel={selectedOption?.label}
                  background={inputBackground}
                  className={dropdownTriggerStyles()}
                />
              }
            />
          </div>
        );
      },
      [filterValues, handleFieldChange, inputBackground],
    );

    const renderSearchField = React.useCallback(
      (field: FilterField) => {
        const fieldValue = filterValues[field.name] || "";

        return (
          <div
            key={field.name}
            className="uipm:flex-1"
            {...(field._editable || {})}
          >
            <SearchField
              placeholder={field.placeholder || "Search by location or venue"}
              value={fieldValue}
              background={inputBackground}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              onSearch={(value) => handleFieldChange(field.name, value)}
              containerClassName="uipm:flex-1 uipm:w-full"
            />
          </div>
        );
      },
      [filterValues, handleFieldChange, inputBackground],
    );

  return (
    <section
      id={id}
      className={sectionWrapper({ backgroundColor, topPadding, bottomPadding })}
      {...props}
    >
      <div className="uipm:w-full uipm:px-[var(--container-margin)]">
        <div className={cn(filterSection(), className)}>
          <div className={filtersLayout()}>
            {fields.map((field) => (
              field.type === "dropdown" ? renderDropdown(field) : renderSearchField(field)
            ))}
          </div>
        </div>
      </div>
    </section>
  );
});

FilterSection.displayName = "FilterSection";
